import React from 'react';
import { ContentRow, VideoContent } from '../../../types/homepage';

interface SmallThumbnailGridProps {
    row: ContentRow;
    onVideoClick?: (videoId: string) => void;
}

const SmallThumbnailGrid: React.FC<SmallThumbnailGridProps> = ({ row, onVideoClick }) => {
    const { content, title, subtitle, settings } = row;
    const itemsPerRow = settings?.itemsPerRow || 4;
    if (!content || content.length === 0) return null;

    return (
        <section
            className='py-8 px-4 sm:px-6 lg:px-8'
            style={{ backgroundColor: settings?.backgroundColor as string }}
        >
            <div className='max-w-7xl mx-auto'>
                {(title || subtitle) && (
                    <div className='mb-6'>
                        {title && (
                            <h2 className='text-2xl md:text-3xl font-bold mb-2' style={{ color: settings?.textColor as string }}>
                                {title}
                            </h2>
                        )}
                        {subtitle && (
                            <p className='text-gray-600' style={{ color: settings?.textColor as string }}>
                                {subtitle}
                            </p>
                        )}
                    </div>
                )}
                <div
                    className='grid gap-4'
                    style={{
                        gridTemplateColumns: `repeat(auto-fill, minmax(${itemsPerRow === 6 ? '180px' : itemsPerRow === 4 ? '250px' : '200px'
                            }, 1fr))`,
                    }}
                >
                    {content.map((video: any) => (
                        <ThumbnailCard key={video.id} video={video} onClick={onVideoClick} settings={settings} />
                    ))}
                </div>
            </div>
        </section>
    );
};

interface ThumbnailCardProps {
    video: VideoContent;
    onClick?: (videoId: string) => void;
    settings?: Record<string, any>;
}

const ThumbnailCard: React.FC<ThumbnailCardProps> = ({ video, onClick, settings }) => {
    return (
        <div onClick={() => onClick?.(video.id)} className='cursor-pointer group'>
            <div className='relative aspect-video bg-gray-200 rounded-lg overflow-hidden mb-2'>
                <img
                    src={video.thumbnail}
                    alt={video.title}
                    className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-300'
                />
                {video.duration && settings?.showDuration && (
                    <div className='absolute bottom-1.5 right-1.5 px-1.5 py-0.5 bg-black/80 text-white text-xs font-semibold rounded'>
                        {video.duration}
                    </div>
                )}
                <div className='absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors'>
                    <div className='w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity'>
                        <svg className='w-5 h-5 text-black ml-0.5' fill='currentColor' viewBox='0 0 20 20'>
                            <path d='M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z' />
                        </svg>
                    </div>
                </div>
            </div>
            <div>
                <h3 className='font-medium text-sm line-clamp-2 mb-1 group-hover:text-blue-600 transition-colors'>
                    {video.title}
                </h3>
                <div className='flex flex-col text-xs text-gray-600'>
                    {video.channelName && <span className='line-clamp-1'>{video.channelName}</span>}
                    {video.views && settings?.showViewCount && (
                        <span>{video.views.toLocaleString()} views</span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SmallThumbnailGrid;